import { messages } from '../constants/messages';
import { client } from '../helpers/client/client';
import { errorLog, generateErrorBody, generatePromiseObjects, isValidTransferSpec, randomUUID, throwError } from '../helpers/helpers';
import { asperaSdk } from '../index';
/**
 * Check if IBM Aspera for Desktop connection works. This function is called by init
 * when initializing the SDK. This function can be used at any point for checking.
 *
 * @returns a promise that resolves if server can connect or rejects if not
 */
export var testConnection = function () {
    return client.request('get_info')
        .then(function (data) {
        asperaSdk.globals.AsperaSdkInfo = data;
        asperaSdk.globals.asperaAppVerified = true;
        return data;
    });
};
/**
 * Initialize drag and drop.
 * @param initCall - Indicate if called via init flow and should not reject
 *
 * @returns a promise that resolves if the initialization was successful or not
 */
export var initDragDrop = function (initCall) {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    var promiseInfo = generatePromiseObjects();
    client.request('init_drag_drop')
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.dragDropInitFailed, error);
        if (initCall) {
            promiseInfo.resolver(false);
            errorLog(messages.dragDropInitFailedInit, error);
        }
        else {
            promiseInfo.rejecter(generateErrorBody(messages.dragDropInitFailed, error));
        }
    });
    return promiseInfo.promise;
};
/**
 * Initialize IBM Aspera client. If client cannot (reject/catch), then
 * client should attempt fixing server URL or trying again. If still fails disable UI elements.
 *
 * @param options initialization options:
 *
 * - `appId` the unique ID for the website. Transfers initiated during this session
 * will be associated with this ID. It is recommended to use a unique ID to keep transfer
 * information private from other websites.
 *
 * - `supportMultipleUsers` when enabled (defaults to false), the SDK will iterate over a port
 * range and generate a session id to determine the running instance of the desktop app for the
 * current user. This is needed when multiple users may be logged into the same machine
 * simultaneously, for example on a Windows Server.
 *
 * @returns a promise that resolves if IBM Aspera Desktop is running properly or
 * rejects if unable to connect
 */
export var init = function (options) {
    var _a, _b;
    var appId = (_a = options === null || options === void 0 ? void 0 : options.appId) !== null && _a !== void 0 ? _a : randomUUID();
    var supportMultipleUsers = (_b = options === null || options === void 0 ? void 0 : options.supportMultipleUsers) !== null && _b !== void 0 ? _b : false;
    asperaSdk.globals.appId = appId;
    if (supportMultipleUsers) {
        asperaSdk.globals.supportMultipleUsers = true;
        asperaSdk.globals.sessionId = randomUUID();
    }
    return asperaSdk.activityTracking.setup()
        .then(function () { return testConnection(); })
        .then(function () { return initDragDrop(true); })
        .catch(function (error) {
        errorLog(messages.serverError, error);
        asperaSdk.globals.asperaAppVerified = false;
        throw generateErrorBody(messages.serverError, error);
    });
};
/**
 * Start a transfer
 *
 * @param transferSpec standard transferSpec for transfer
 * @param asperaSdkSpec IBM Aspera settings when starting a transfer
 *
 * @returns a promise that resolves if transfer initiation is successful and rejects if transfer cannot be started
 */
export var startTransfer = function (transferSpec, asperaSdkSpec) {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    if (!isValidTransferSpec(transferSpec)) {
        return throwError(messages.notValidTransferSpec, { transferSpec: transferSpec });
    }
    var promiseInfo = generatePromiseObjects();
    var payload = {
        transfer_spec: transferSpec,
        desktop_spec: asperaSdkSpec,
        app_id: asperaSdk.globals.appId,
    };
    client.request('start_transfer', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.transferFailed, error);
        promiseInfo.rejecter(generateErrorBody(messages.transferFailed, error));
    });
    return promiseInfo.promise;
};
/**
 * Register a callback event for getting transfer updates
 *
 * @param callback callback function to receive transfers
 *
 * @returns ID representing the callback for deregistration purposes
 */
export var registerActivityCallback = function (callback) {
    return asperaSdk.activityTracking.setCallback(callback);
};
/**
 * Remove a callback from the transfer callback
 *
 * @param id the ID returned by `registerActivityCallback`
 */
export var deregisterActivityCallback = function (id) {
    asperaSdk.activityTracking.removeCallback(id);
};
/**
 * Register a callback event for when a user removes or cancels a transfer
 * directly from IBM Aspera. This may also be called if IBM Aspera
 * is configured to automatically remove completed transfers.
 *
 * @param callback callback function to receive transfers
 *
 * @returns ID representing the callback for deregistration purposes
 */
export var registerRemovedCallback = function (callback) {
    return asperaSdk.activityTracking.setRemovedCallback(callback);
};
/**
 * Remove a callback from the removed transfer callback
 *
 * @param id the ID returned by `registerRemovedCallback`
 */
export var deregisterRemovedCallback = function (id) {
    asperaSdk.activityTracking.removeRemovedCallback(id);
};
/**
 * Register a callback for getting updates about the connection status of IBM Aspera SDK.
 *
 * For example, to be notified of when the SDK loses connection with the application or connection
 * is re-established. This can be useful if you want to handle the case where the user quits IBM Aspera
 * after `init` has already been called, and want to prompt the user to relaunch the application.
 *
 * @param callback callback function to receive events
 *
 * @returns ID representing the callback for deregistration purposes
 */
export var registerStatusCallback = function (callback) {
    return asperaSdk.activityTracking.setWebSocketEventCallback(callback);
};
/**
 * Remove a callback from getting connection status events.
 *
 * @param id the ID returned by `registerStatusCallback`
 */
export var deregisterStatusCallback = function (id) {
    asperaSdk.activityTracking.removeWebSocketEventCallback(id);
};
/**
 * Register a callback for getting updates about the Safari extension status.
 *
 * This can be useful if you want to handle the case where the user enable or disable the Safari extension.
 *
 * @param callback callback function to receive events
 *
 * @returns ID representing the callback for deregistration purposes
 */
export var registerSafariExtensionStatusCallback = function (callback) {
    return asperaSdk.activityTracking.setSafariExtensionEventCallback(callback);
};
/**
 * Remove a callback from getting Safari extension status events.
 *
 * @param id the ID returned by `registerStatusCallback`
 */
export var deregisterSafariExtensionStatusCallback = function (id) {
    asperaSdk.activityTracking.removeSafariExtensionEventCallback(id);
};
/**
 * Remove a transfer. This will stop the transfer if it is in progress.
 *
 * @param id transfer uuid
 *
 * @returns a promise that resolves if transfer is removed and rejects if transfer cannot be removed
 */
export var removeTransfer = function (id) {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    var promiseInfo = generatePromiseObjects();
    var payload = {
        transfer_id: id,
    };
    client.request('remove_transfer', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.removeTransferFailed, error);
        promiseInfo.rejecter(generateErrorBody(messages.removeTransferFailed, error));
    });
    return promiseInfo.promise;
};
/**
 * Stop a transfer.
 *
 * @param id transfer uuid
 *
 * @returns a promise that resolves if transfer is stopped and rejects if transfer cannot be stopped
 */
export var stopTransfer = function (id) {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    var promiseInfo = generatePromiseObjects();
    var payload = {
        transfer_id: id,
    };
    client.request('stop_transfer', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.stopTransferFailed, error);
        promiseInfo.rejecter(generateErrorBody(messages.stopTransferFailed, error));
    });
    return promiseInfo.promise;
};
/**
 * Resume a paused or failed transfer.
 *
 * @param id transfer uuid
 * @param options resume transfer options
 *
 * @returns a promise that resolves with the new transfer object if transfer is resumed
 */
export var resumeTransfer = function (id, options) {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    var promiseInfo = generatePromiseObjects();
    var payload = {
        transfer_id: id,
        transfer_spec: options,
    };
    client.request('resume_transfer', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.resumeTransferFailed, error);
        promiseInfo.rejecter(generateErrorBody(messages.resumeTransferFailed, error));
    });
    return promiseInfo.promise;
};
/**
 * Displays a file browser dialog for the user to select files.
 *
 * @param options file dialog options
 *
 * @returns a promise that resolves with the selected file(s) and rejects if user cancels dialog
 */
export var showSelectFileDialog = function (options) {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    var promiseInfo = generatePromiseObjects();
    var payload = {
        options: options || {},
        app_id: asperaSdk.globals.appId,
    };
    client.request('show_file_dialog', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.showSelectFileDialogFailed, error);
        promiseInfo.rejecter(generateErrorBody(messages.showSelectFileDialogFailed, error));
    });
    return promiseInfo.promise;
};
/**
 * Displays a folder browser dialog for the user to select folders.
 *
 * @param options folder dialog options
 *
 * @returns a promise that resolves with the selected folder(s) and rejects if user cancels dialog
 */
export var showSelectFolderDialog = function (options) {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    var promiseInfo = generatePromiseObjects();
    var payload = {
        options: options || {},
        app_id: asperaSdk.globals.appId,
    };
    client.request('show_folder_dialog', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.showSelectFolderDialogFailed, error);
        promiseInfo.rejecter(generateErrorBody(messages.showSelectFolderDialogFailed, error));
    });
    return promiseInfo.promise;
};
/**
 * Opens the IBM Aspera preferences page.
 *
 * @returns a promise that resolves when the preferences page is opened.
 */
export var showPreferences = function () {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    var promiseInfo = generatePromiseObjects();
    client.request('open_preferences')
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.showPreferencesFailed, error);
        promiseInfo.rejecter(generateErrorBody(messages.showPreferencesFailed, error));
    });
    return promiseInfo.promise;
};
/**
 * Get all transfers associated with the current application.
 *
 * @returns a promise that resolves with an array of transfers.
 */
export var getAllTransfers = function () {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    var promiseInfo = generatePromiseObjects();
    var payload = {
        app_id: asperaSdk.globals.appId,
    };
    client.request('get_all_transfers', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.getAllTransfersFailed, error);
        promiseInfo.rejecter(generateErrorBody(messages.getAllTransfersFailed, error));
    });
    return promiseInfo.promise;
};
/**
 * Get a specific transfer by ID.
 *
 * @param id transfer uuid
 *
 * @returns a promise that resolves with the transfer.
 */
export var getTransfer = function (id) {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    var promiseInfo = generatePromiseObjects();
    var payload = {
        transfer_id: id,
    };
    client.request('get_transfer', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.getTransferFailed, error);
        promiseInfo.rejecter(generateErrorBody(messages.getTransferFailed, error));
    });
    return promiseInfo.promise;
};
/**
 * Opens and highlights the downloaded file in Finder or Windows Explorer. If multiple files,
 * then only the first file will be selected.
 *
 * @param id transfer uuid
 *
 * @returns a promise that resolves if the file can be shown and rejects if not
 */
export var showDirectory = function (id) {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    var promiseInfo = generatePromiseObjects();
    var payload = {
        transfer_id: id,
    };
    client.request('show_directory', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.showDirectoryFailed, error);
        promiseInfo.rejecter(generateErrorBody(messages.showDirectoryFailed, error));
    });
    return promiseInfo.promise;
};
/**
 * Modify the speed of a running transfer.
 *
 * @param id transfer uuid
 * @param options transfer rate options
 *
 * @returns a promise that resolves if the transfer rate can be modified and rejects if not
 */
export var modifyTransfer = function (id, options) {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    var promiseInfo = generatePromiseObjects();
    var payload = {
        transfer_id: id,
        transfer_spec: options,
    };
    client.request('modify_transfer', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.modifyTransferFailed, error);
        promiseInfo.rejecter(generateErrorBody(messages.modifyTransferFailed, error));
    });
    return promiseInfo.promise;
};
/**
 * Set the custom branding template to be used by IBM Aspera. If the app is already
 * configured to use a different branding, then the branding template you specify will be
 * stored by the app, allowing the end user to switch at any point.
 *
 * @param id custom branding template id. This should be consistent across page loads.
 * @param options custom branding options
 *
 * @returns a promise that resolves if the branding was properly set.
 */
export var setBranding = function (id, options) {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    var promiseInfo = generatePromiseObjects();
    var branding = {
        id: id,
        name: options.name,
        theme: options.theme,
    };
    var payload = {
        branding: branding,
    };
    client.request('update_branding', payload)
        .then(function (data) { return promiseInfo.resolver(data); })
        .catch(function (error) {
        errorLog(messages.setBrandingFailed, error);
        promiseInfo.rejecter(generateErrorBody(messages.setBrandingFailed, error));
    });
    return promiseInfo.promise;
};
/**
 * Create a dropzone for the given element selector.
 *
 * @param callback the function to call once the files are dropped
 * @param elementSelector the selector of the element on the page that should watch for drop events
 */
export var createDropzone = function (callback, elementSelector) {
    var elements = document.querySelectorAll(elementSelector);
    if (!elements || !elements.length) {
        errorLog(messages.unableToFindElementOnPage);
        return;
    }
    var dragEvent = function (event) {
        event.preventDefault();
    };
    var dropEvent = function (event) {
        event.preventDefault();
        var files = [];
        if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length && event.dataTransfer.files[0]) {
            for (var i = 0; i < event.dataTransfer.files.length; i++) {
                var file = event.dataTransfer.files[i];
                files.push({
                    lastModified: file.lastModified,
                    name: file.name,
                    size: file.size,
                    type: file.type
                });
            }
            var payload = {
                files: files,
                app_id: asperaSdk.globals.appId,
            };
            client.request('dropped_files', payload)
                .then(function (data) { return callback({ event: event, files: data }); })
                .catch(function (error) {
                errorLog(messages.unableToReadDropped, error);
            });
        }
    };
    elements.forEach(function (element) {
        element.addEventListener('dragover', dragEvent);
        element.addEventListener('drop', dropEvent);
        asperaSdk.globals.dropZonesCreated.set(elementSelector, [{ event: 'dragover', callback: dragEvent }, { event: 'drop', callback: dropEvent }]);
    });
};
/**
 * Remove dropzone.
 *
 * @param elementSelector the selector of the element on the page that should remove
 */
export var removeDropzone = function (elementSelector) {
    var foundDropzone = asperaSdk.globals.dropZonesCreated.get(elementSelector);
    if (foundDropzone) {
        foundDropzone.forEach(function (data) {
            var elements = document.querySelectorAll(elementSelector);
            if (elements && elements.length) {
                elements.forEach(function (element) {
                    element.removeEventListener(data.event, data.callback);
                });
            }
        });
    }
};
/**
 * Get metadata about the IBM Aspera installation.
 *
 * @returns a promise that returns information about the user's IBM Aspera installation.
 */
export var getInfo = function () {
    if (!asperaSdk.isReady) {
        return throwError(messages.serverNotVerified);
    }
    return new Promise(function (resolve, _) {
        resolve(asperaSdk.globals.AsperaSdkInfo);
    });
};
